import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  History,
  Brain,
  Home,
  MoreHorizontal,
  Minimize2,
  Maximize2,
  SquarePen,
  Copy,
  FileText,
  FileDown,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ChatIconButton from './ChatIconButton';
import ActionMenuItem from './ActionMenuItem';
import {
  chatHeader,
  chatTitleClass,
  menuDropdown,
  ghostIconButton,
  scopeLabelClass,
} from './chatViewTheme';
import { dropdown } from '../ui/uiTheme';

export default function AIChatPanelHeader({
  reduceMotion,
  isNarrowPanel,
  currentThreadTitle,
  messagesCount,
  onClearChat,
  actionMenuRef,
  actionMenuOpen,
  onToggleActionMenu,
  threadsOpen,
  onToggleThreads,
  memoryPanelOpen,
  onToggleMemory,
  hasExportableMessages,
  onCopyConversation,
  onExportMarkdown,
  onExportPdf,
  panelWidth,
  widePanelWidth,
  onTogglePanelWidth,
  onClose,
  selectedAgentId,
  onAgentChange,
  agents,
  loading,
  navigationContextEnabled,
  scopeChip,
  scopeOverride,
  onOpenScopePicker,
  ragContextEnabled,
  indexHint,
}: any) {
  return (
    <motion.div className={chatHeader}>
      <motion.div className="flex items-center gap-2 px-3 py-2.5">
        <img
          src="/assets/Vanti2.png"
          alt=""
          className="h-8 w-8 shrink-0 object-contain"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <h2 className={chatTitleClass}>Vanti</h2>
          {!isNarrowPanel ? (
            currentThreadTitle ? (
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentThreadTitle}
                  initial={reduceMotion ? false : { opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? undefined : { opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="max-w-[180px] truncate text-xs text-slate-500"
                  title={currentThreadTitle}
                >
                  {currentThreadTitle}
                </motion.p>
              </AnimatePresence>
            ) : (
              <p className="truncate text-[11px] leading-tight text-slate-400">
                Your VantagePoint copilot
              </p>
            )
          ) : null}
        </div>
        <motion.div layout={false} className="flex shrink-0 items-center gap-0.5">
          <motion.button
            type="button"
            onClick={onClearChat}
            disabled={messagesCount === 0}
            whileHover={reduceMotion || messagesCount === 0 ? undefined : { scale: 1.08 }}
            whileTap={reduceMotion || messagesCount === 0 ? undefined : { scale: 0.92 }}
            className={`rounded-lg p-2 transition-colors ${
              messagesCount === 0
                ? 'cursor-not-allowed bg-dark-border text-slate-600 opacity-30'
                : `${ghostIconButton} bg-dark-elevated hover:text-brand-purple-light`
            }`}
            aria-label="New conversation"
            title="New conversation"
          >
            <SquarePen className="h-4 w-4" aria-hidden />
          </motion.button>
          <Link
            to="/"
            className={`${ghostIconButton} rounded-lg p-2 transition-colors bg-dark-elevated hover:text-brand-teal-light`}
            aria-label="Go to dashboard"
            title="Dashboard"
          >
            <Home className="h-4 w-4" aria-hidden />
          </Link>
          <motion.div ref={actionMenuRef} className="relative">
            <ChatIconButton
              onClick={onToggleActionMenu}
              label="Chat options"
              title="Options"
              className={actionMenuOpen ? 'bg-dark-elevated text-slate-200' : ''}
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden />
            </ChatIconButton>
            <AnimatePresence>
              {actionMenuOpen && (
                <motion.div
                  initial={reduceMotion ? false : { opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={reduceMotion ? undefined : { opacity: 0, scale: 0.95, y: -4 }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                  className={`absolute right-0 top-full z-20 mt-1.5 w-52 origin-top-right ${menuDropdown}`}
                >
                  <ActionMenuItem
                    icon={History}
                    label="Conversation history"
                    variant="purple"
                    active={threadsOpen}
                    onClick={onToggleThreads}
                  />
                  <ActionMenuItem
                    icon={Brain}
                    label="Vanti memory"
                    variant="green"
                    active={memoryPanelOpen}
                    onClick={onToggleMemory}
                  />
                  <div className={dropdown.separator} />
                  <ActionMenuItem
                    icon={Copy}
                    label="Copy conversation"
                    variant="purple"
                    disabled={!hasExportableMessages}
                    onClick={onCopyConversation}
                  />
                  <ActionMenuItem
                    icon={FileText}
                    label="Export as Markdown"
                    variant="purple"
                    disabled={!hasExportableMessages}
                    onClick={onExportMarkdown}
                  />
                  <ActionMenuItem
                    icon={FileDown}
                    label="Export as PDF"
                    variant="purple"
                    disabled={!hasExportableMessages}
                    onClick={onExportPdf}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <ChatIconButton
            onClick={onTogglePanelWidth}
            label={panelWidth >= widePanelWidth - 10 ? 'Collapse panel' : 'Expand panel'}
            title={panelWidth >= widePanelWidth - 10 ? 'Collapse' : 'Expand'}
          >
            {panelWidth >= widePanelWidth - 10 ? (
              <Minimize2 className="h-4 w-4" aria-hidden />
            ) : (
              <Maximize2 className="h-4 w-4" aria-hidden />
            )}
          </ChatIconButton>

          <ChatIconButton onClick={onClose} label="Close">
            <X className="h-4 w-4" aria-hidden />
          </ChatIconButton>
        </motion.div>
      </motion.div>

      <div className="border-t border-dark-border px-3 py-2">
        <label className="sr-only" htmlFor="vanti-agent-select">
          Agent
        </label>
        <select
          id="vanti-agent-select"
          className="select-mdl w-full text-sm leading-normal"
          value={selectedAgentId}
          onChange={onAgentChange}
          disabled={loading}
          title="Select agent"
        >
          <option value="">Vanti (built-in)</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.visibility === 'org' ? ' (Org)' : ''}
            </option>
          ))}
        </select>
      </div>

      {navigationContextEnabled ? (
        <div className="border-t border-dark-border px-3 py-2.5">
          <div className="flex items-center gap-2 rounded-lg bg-dark-base px-3 py-2 border border-dark-border">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-brand-purple/20">
              <svg
                className="h-3 w-3 text-brand-purple-light"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10l4.553-2.069A1 1 0 0121 8.82V15a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium text-slate-400 leading-none mb-0.5">Context</p>
              <p className="truncate text-xs text-slate-200">{scopeChip || 'No page context'}</p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-md border border-dark-border bg-dark-elevated px-2 py-1 text-[11px] font-medium text-brand-purple-light hover:border-brand-purple/40 hover:bg-brand-purple/10 transition-all"
              onClick={onOpenScopePicker}
            >
              {scopeOverride ? 'Change' : 'Pick scope'}
            </button>
          </div>
          {!ragContextEnabled || indexHint ? (
            <div className="mt-1.5 space-y-0.5 px-1">
              {!ragContextEnabled ? (
                <p className="truncate text-[11px] text-amber-300 flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" aria-hidden />
                  Code/docs search is off for your org.
                </p>
              ) : null}
              {indexHint ? (
                <p className={`truncate text-[11px] ${scopeLabelClass} flex items-center gap-1`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-dark-border shrink-0" aria-hidden />
                  {indexHint}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </motion.div>
  );
}
